import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Modal,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 240;

// ModalComponent with added Cancel button
const ModalComponent = ({ open, handleClose, handleCancel, title, message, onConfirm }) => {
  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', sm: '500px' },
          borderRadius: '25px',
          p: 4,
          zIndex: 1600,
        }}
      >
        <Box
          sx={{
            bgcolor: '#B6B09F',
            borderRadius: '25px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              bgcolor: '#063552',

              p: 2,
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              id="modal-title"
              variant="h6"
              component="h2"
              sx={{
                color: 'white',
                textTransform: 'capitalize',
                fontFamily: 'Nunito',
                fontSize: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <span>{title}⚠️</span>
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'white',
              p: 2,
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px',
              zIndex: 1,
            }}
          >
            <Typography
              id="modal-description"
              sx={{ color: 'black', mb: 2, textAlign: 'left' }}
            >
              {message}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                mt: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  width: '80px !important',
                  minWidth: '80px !important',
                  maxWidth: '80px !important',
                  height: '30px !important',
                  color: 'blue',
                  border: 'none',
                  '&:hover': {
                    color: 'darkblue',
                    border: 'none',
                  },
                  padding: '8px 16px',
                  flex: '0 0 auto',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={onConfirm}
                sx={{
                  width: '50px !important',
                  minWidth: '50px !important',
                  maxWidth: '50px !important',
                  height: '30px !important',
                  color: 'red',
                  border: 'none !important',
                  '&:hover': {
                    color: 'green',
                  },
                  borderRadius: '25px',
                  padding: '8px 16px',
                  flex: '0 0 auto',
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

function CreateAssignment() {
  const [assignment, setAssignment] = useState({
    subject: '',
    className: '',
    section: '',
    creationDate: new Date().toISOString().slice(0, 10),
    submissionDate: '',
    details: '',
    files: [],
  });
  const [notification, setNotification] = useState('');
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [checkedAssignments, setCheckedAssignments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [selectedClassName, setSelectedClassName] = useState('');
  const [selectedSectionName, setSelectedSectionName] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
const [openDescId, setOpenDescId] = useState(null);

  const navigate = useNavigate();

  // Helper function to parse DD/MM/YYYY date format
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Handle form class/section changes for assignment creation
  useEffect(() => {
    if (assignment.className) {
      fetchSections(assignment.className);
    } else {
      setSections([]);
      setAssignment((prev) => ({ ...prev, section: '', subject: '' }));
    }
  }, [assignment.className]);

  useEffect(() => {
    if (assignment.className && assignment.section) {
      fetchSubjects(assignment.className, assignment.section);
    } else {
      setSubjects([]);
      setAssignment((prev) => ({ ...prev, subject: '' }));
    }
  }, [assignment.className, assignment.section]);

  // Handle filter changes for table display
  useEffect(() => {
    if (selectedClass && selectedSection) {
      setIsLoadingSubjects(true);
      fetchSubjects(selectedClass, selectedSection).finally(() => {
        setIsLoadingSubjects(false);
      });
    } else {
      setSubjects([]);
      setSelectedSubject('');
    }
  }, [selectedClass, selectedSection]);

  // Fetch assignments only when all required filters are selected
  useEffect(() => {
    const shouldFetchAssignments = selectedTeacher && selectedClass && selectedSection && selectedSubject;
    
    if (shouldFetchAssignments) {
      setIsLoadingAssignments(true);
      fetchAssignments().finally(() => {
        setIsLoadingAssignments(false);
      });
    } else {
      if (!selectedTeacher || !selectedClass || !selectedSection || !selectedSubject) {
        setAssignments([]);
        setFilteredAssignments([]);
      }
    }
  }, [selectedTeacher, selectedClass, selectedSection, selectedSubject]);

  // Filter assignments by selected subject name and sort by assignmentdate (latest first)
  useEffect(() => {
    if (assignments.length > 0 && selectedSubject) {
      const filtered = assignments
        .filter(assignment => assignment.subjectName === selectedSubject)
        .sort((a, b) => parseDate(b.assignmentdate) - parseDate(a.assignmentdate));
      setFilteredAssignments(filtered);
    } else {
      setFilteredAssignments([]);
    }
  }, [assignments, selectedSubject]);

  const fetchTeachers = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetTeacher', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) throw new Error('Unauthorized: Invalid or expired token.');
        else if (response.status === 404) throw new Error('No teachers found.');
        else throw new Error(`Failed to fetch teachers: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      const teachersData = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      if (teachersData.length === 0) throw new Error('No teacher data available.');
      setTeachers(teachersData);
    } catch (error) {
      console.error('Error fetching teachers:', error.message);
      setTeachers([]);
      setSnackbarMessage(error.message || 'An error occurred while fetching teachers.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      const teacherId = selectedTeacher || sessionStorage.getItem('employeeId');
      if (!teacherId) throw new Error('No teacher selected or employee ID missing.');
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetClass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ teacherId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) throw new Error('Unauthorized: Invalid or expired token.');
        else if (response.status === 404) throw new Error('No classes found.');
        else throw new Error(`Failed to fetch classes: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      const classesData = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      setClasses(classesData);
      if (classesData.length === 0) {
        setSnackbarMessage('No classes found.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching classes:', error.message);
      setClasses([]);
      setSnackbarMessage(error.message || 'An error occurred while fetching classes.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      if (!classId) throw new Error('Class ID is missing.');
      const teacherId = selectedTeacher || sessionStorage.getItem('employeeId');
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/ddlSection_clsId', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ teacherId, classId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 401) throw new Error('Unauthorized: Invalid or expired token.');
        else if (response.status === 404) throw new Error('No sections found.');
        else throw new Error(`Failed to fetch sections: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      const sectionsData = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      setSections(sectionsData);
      if (sectionsData.length === 0) {
        setSnackbarMessage('No sections found.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching sections:', error.message);
      setSections([]);
      setSnackbarMessage(error.message || 'An error occurred while fetching sections.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchSubjects = async (classId, sectionId) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      if (!classId || !sectionId) throw new Error('Class ID or Section ID is missing.');
      const teacherId = selectedTeacher || sessionStorage.getItem('employeeId');
      console.log('Fetching subjects with payload:', { teacherId, classId, sectionId });
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/ddlSubject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ teacherId, classId, sectionId }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch subjects failed:', errorText);
        if (response.status === 401) throw new Error('Unauthorized: Invalid or expired token.');
        else if (response.status === 404) throw new Error('No subjects found.');
        else throw new Error(`Failed to fetch subjects: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      console.log('Subjects data received:', data);
      const subjectsData = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      setSubjects(subjectsData);
      if (subjectsData.length === 0) {
        setSnackbarMessage('No subjects found.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error.message);
      setSubjects([]);
      setSnackbarMessage(error.message || 'An error occurred while fetching subjects.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchAssignments = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing. Please log in again.');
      const payload = {
        teacherId: selectedTeacher || sessionStorage.getItem('employeeId'),
        classId: selectedClass,
        sectionId: selectedSection,
        subjectId: subjects.find(sub => sub.subjectName === selectedSubject)?.subjectId || ''
      };
      console.log('Fetching assignments with payload:', payload);
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/GetAssignment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fetch assignments failed:', errorText);
        if (response.status === 401) throw new Error('Unauthorized: Invalid or expired token.');
        else if (response.status === 404) throw new Error('No assignments found.');
        else throw new Error(`Failed to fetch assignments: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      console.log('Assignments data received:', data);
      const assignmentsData = (Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [])
        .sort((a, b) => parseDate(b.assignmentdate) - parseDate(a.assignmentdate));
      setAssignments(assignmentsData);
      
      if (assignmentsData.length === 0) {
        setSnackbarMessage('No assignments found for the selected filters.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error.message);
      setAssignments([]);
      setSnackbarMessage(error.message || 'An error occurred while fetching assignments.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAssignment((prevAssignment) => ({ ...prevAssignment, [name]: value }));
    
    if (name === 'className') {
      setSelectedClass(value);
      setSelectedClassName(classes.find((cls) => cls.classId === value)?.className || '');
      setSections([]);
      setSubjects([]);
      setSelectedSection('');
      setSelectedSubject('');
    }
    if (name === 'section') {
      setSelectedSection(value);
      setSelectedSectionName(sections.find((sec) => sec.sectionId === value)?.sectionName || '');
      setSubjects([]);
      setSelectedSubject('');
    }
    if (name === 'subject') {
      const selectedSubjectName = subjects.find((sub) => sub.subjectName === value)?.subjectName || value;
      setSelectedSubject(selectedSubjectName);
      console.log('Selected Subject Name:', selectedSubjectName);
    }
  };

  const handleTeacherChange = (e) => {
    const teacherId = e.target.value;
    setSelectedTeacher(teacherId);
    setSelectedClass('');
    setSelectedSection('');
    setSelectedSubject('');
    setClasses([]);
    setSections([]);
    setSubjects([]);
    setAssignments([]);
    setFilteredAssignments([]);
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    setSelectedClass(classId);
    setSelectedClassName(classes.find((cls) => cls.classId === classId)?.className || '');
    setSelectedSection('');
    setSelectedSubject('');
    setSections([]);
    setSubjects([]);
    setAssignments([]);
    setFilteredAssignments([]);
  };

  const handleSectionChange = (e) => {
    const sectionId = e.target.value;
    setSelectedSection(sectionId);
    setSelectedSectionName(sections.find((sec) => sec.sectionId === sectionId)?.sectionName || '');
    setSelectedSubject('');
    setSubjects([]);
    setAssignments([]);
    setFilteredAssignments([]);
  };

  const handleSubjectChange = (e) => {
    const subjectName = e.target.value;
    setSelectedSubject(subjectName);
    console.log('Selected Subject Name:', subjectName);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAssignment((prevAssignment) => ({
      ...prevAssignment,
      files: [...prevAssignment.files, ...files],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const postAssignmentNotification = async () => {
    const userId = sessionStorage.getItem('employeeId');
    const message = `A new assignment has been created for ${selectedClassName} - ${selectedSectionName}`;
    const clientName = sessionStorage.getItem('clientName');
    const name = clientName.replace(/['"~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/g, '');
    try {
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Notifications/PostAssignmentNotification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
        body: JSON.stringify({ userId, message, name }),
      });
      if (response.ok) {
        console.log('Notification sent successfully.');
      } else {
        console.error('Failed to send notification.');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { subject, className, section, creationDate, submissionDate, files } = assignment;
    if (!subject || !className || !section || !creationDate || !submissionDate) {
      setSnackbarMessage('All fields except Details are required.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    try {
      const formData = new FormData();
      formData.append('ClassId', className);
      formData.append('SectionId', section);
      formData.append('SubjectId', subjects.find(sub => sub.subjectName === subject)?.subjectId || '');
      formData.append('TeacherId', sessionStorage.getItem('employeeId'));
      formData.append('AssignmentDescription', assignment.details || ' ');
      formData.append('AssignmentDate', formatDate(creationDate));
      formData.append('AssignmentSubmitDate', formatDate(submissionDate));
      files.forEach((file) => formData.append('AssignMentFile', file));
      
      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/Teacher/CreateAssignment', {
        method: 'POST',
        headers: { Authorization: sessionStorage.getItem('token') },
        body: formData,
      });
      
      if (response.ok) {
        setSnackbarMessage('Assignment has been created successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setAssignment({
          subject: '',
          className: '',
          section: '',
          creationDate: new Date().toISOString().slice(0, 10),
          submissionDate: '',
          details: '',
          files: [],
        });
        setSelectedClassName('');
        setSelectedSectionName('');
        postAssignmentNotification();
        if (selectedTeacher && selectedClass && selectedSection && selectedSubject) {
          fetchAssignments();
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.error('Failed to create assignment:', await response.text());
        setSnackbarMessage('Failed to create assignment.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      setSnackbarMessage('An error occurred while creating the assignment.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCheckboxChange = (assignment) => {
    setCheckedAssignments((prev) =>
      prev.some((a) => a.assignmentId === assignment.assignmentId)
        ? prev.filter((a) => a.assignmentId !== assignment.assignmentId)
        : [...prev, assignment]
    );
  };

  const handleDescriptionChange = (e, assignmentId) => {
    setFilteredAssignments((prev) =>
      prev.map((a) =>
        a.assignmentId === assignmentId ? { ...a, assignmentDescription: e.target.value } : a
      )
    );
    console.log(`Description changed for assignment ${assignmentId}: ${e.target.value}`);
  };

  const handleViewFile = (filePath) => {
    window.open(`https://wsmsv1.aidigitalsec.com/${filePath}`, '_blank', 'noopener,noreferrer');
  };

  const handleEdit = (assignment) => {
    try {
      console.log('Navigating to AssignmentEdit with assignmentId:', assignment.assignmentId);
      navigate(`/assignmentedit/${assignment.assignmentId}`);
    } catch (error) {
      console.error('Error navigating to AssignmentEdit:', error);
      setSnackbarMessage('Error navigating to edit page.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = (assignmentId) => {
    setSelectedAssignmentId(assignmentId);
    setModalOpen(true);
  };

  const handleDeleteConfirmed = async (assignmentId) => {
    try {
      const response = await fetch(`https://wsmsv1.aidigitalsec.com/api/AssignmentCreate/Id?assignmentId=${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: sessionStorage.getItem('token') },
      });
      if (response.ok) {
        console.log('Assignment deleted successfully');
        setSnackbarMessage('Assignment deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchAssignments();
      } else {
        console.error('Error deleting assignment:', await response.text());
        setSnackbarMessage('Error deleting assignment.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setSnackbarMessage('Error deleting assignment.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setModalOpen(false);
  };

  const handleCancelModal = () => {
    setModalOpen(false);
    setSelectedAssignmentId(null);
  };

  const getTableContent = () => {
    if (isLoadingAssignments) {
      return (
        <TableRow>
          <TableCell colSpan={9} sx={{ textAlign: 'center', padding: '20px', fontSize: '1.1rem' }}>
            Loading assignments...
          </TableCell>
        </TableRow>
      );
    }

    if (!selectedTeacher || !selectedClass || !selectedSection || !selectedSubject) {
      return (
        <TableRow>
          <TableCell colSpan={9} sx={{ textAlign: 'center', padding: '20px', fontSize: '1.1rem' }}>
            Please select Teacher, Class, Section, and Subject to view assignments
          </TableCell>
        </TableRow>
      );
    }

    if (filteredAssignments.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} sx={{ textAlign: 'center', padding: '20px', fontSize: '1.1rem' }}>
            No assignments found for the selected subject
          </TableCell>
        </TableRow>
      );
    }

    return filteredAssignments.map((assignment) => (
      <TableRow key={assignment.assignmentId}>
        <TableCell sx={{ width: '10%', minWidth: 100, fontSize: '1.1rem' }}>
          <Checkbox
            checked={checkedAssignments.some((a) => a.assignmentId === assignment.assignmentId)}
            onChange={() => handleCheckboxChange(assignment)}
          />
        </TableCell>
      {/*  <TableCell sx={{ width: '10%', minWidth: 60, maxWidth: 80, fontSize: '1.1rem' }}>
          {assignment.assignmentId}
        </TableCell> */}
        <TableCell sx={{ width: '15%', minWidth: 150, fontSize: '1.1rem' }}>
          {assignment.assignmentdate}
        </TableCell>
        <TableCell sx={{ width: '15%', minWidth: 150, fontSize: '1.1rem' }}>
          {assignment.subjectName}
        </TableCell>
        <TableCell sx={{ width: '15%', minWidth: 150, fontSize: '1.1rem' }}>
          {assignment.submissiondate}
        </TableCell>
        <TableCell sx={{ width: '15%', minWidth: 150, fontSize: '1.1rem', position: 'relative' }}>
  <Box
    onClick={() =>
      setOpenDescId(
        openDescId === assignment.assignmentId
          ? null
          : assignment.assignmentId
      )
    }
    sx={{
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      cursor: 'pointer',
      color: '#063552',

      fontWeight: 500,
    }}
  >
    {assignment.assignmentDescription}
  </Box>

  {/* 🔵 CLICK POPUP */}
  {openDescId === assignment.assignmentId && (
    <Box
      sx={{
        position: 'absolute',
        top: '-10px',
        left: '50%',
        transform: 'translate(-50%, -100%)',
        backgroundColor: '#063552',

        color: '#fff',
        padding: '10px 14px',
        borderRadius: '6px',
        fontSize: '0.95rem',
        minWidth: '260px',
        maxWidth: '320px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        zIndex: 2000,

        '&::after': {
          content: '""',
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '6px',
          borderStyle: 'solid',
          borderColor: '#063552 transparent transparent transparent',

        },
      }}
    >
      {assignment.assignmentDescription}
    </Box>
  )}
</TableCell>

        <TableCell sx={{ width: '7%', minWidth: 40, maxWidth: 90, fontSize: '1.1rem' }}>
          {classes.find(cls => cls.classId === (assignment.classId || assignment.class))?.className || (assignment.class || 'Unknown')}
        </TableCell>
        <TableCell sx={{ width: '15%', minWidth: 150, fontSize: '1.1rem' }}>
          {assignment.assignmentFilePath ? (
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ width: '100%' }}
              onClick={() => handleViewFile(assignment.assignmentFilePath)}
            >
              View
            </Button>
          ) : (
            <Button
              sx={{
                width: '100%',
                fontSize: '1rem !important',
                bgcolor: '#18A558',
                '&:hover': { bgcolor: '#007bff' },
                color: 'white!important',
                textAlign: 'right',
              }}
              disabled
            >
              Not Submit
            </Button>
          )}
        </TableCell>
       <TableCell sx={{ width: '26%', minWidth: 150, maxWidth: 250, fontSize: '1.1rem' }}>
  <Button
    variant="contained"
    color="warning"
    size="small"
    startIcon={<EditIcon />}
    onClick={() => handleEdit(assignment)}
    sx={{ mb: 1 }}   // ✅ ONLY SPACE BETWEEN EDIT & DELETE
  >
    Edit
  </Button>

  <Button
    variant="contained"
    color="error"
    size="small"
    startIcon={<DeleteIcon />}
    onClick={() => handleDelete(assignment.assignmentId)}
    sx={{ minWidth: '90px', maxWidth: '100px' }}
  >
    Delete
  </Button>
</TableCell>

      </TableRow>
    ));
  };

  return (
    <Box component="main" sx={{
      width: { md: `calc(100% - ${drawerWidth}px)` },
      ml: { md: `${drawerWidth}px` },
      mt: { xs: 0, sm: '64px' },
      p: { xs: 2, md: 4 },
      backgroundColor: '#f8fafc',
    }}>
      <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', color: '#063552'
, textAlign: 'center' }}>
            CREATE ASSIGNMENT

          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Class</InputLabel>
                <Select
                  name="className"
                  value={assignment.className}
                  onChange={handleChange}
                  onFocus={() => fetchClasses()}
                  label="Class"
                  required
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Section</InputLabel>
                <Select
                  name="section"
                  value={assignment.section}
                  onChange={handleChange}
                  label="Section"
                  required
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.sectionId} value={sec.sectionId}>
                      {sec.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!assignment.section}>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subject"
                  value={assignment.subject}
                  onChange={handleChange}
                  label="Subject"
                >
                  <MenuItem value="">Select Subject</MenuItem>
                  {subjects.map((sub) => (
                    <MenuItem key={sub.subjectId} value={sub.subjectName}>
                      {sub.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Upload Files (PDF, JPEG, PNG, JPG)</Typography>
                <Button variant="outlined" component="label" fullWidth>
                  Choose Files
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpeg,.png,.jpg"
                    multiple
                    onChange={handleFileChange}
                  />
                </Button>
                {assignment.files.length > 0 && (
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    {assignment.files.length} file(s) selected
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Creation Date"
                type="date"
                name="creationDate"
                value={assignment.creationDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Submission Date"
                type="date"
                name="submissionDate"
                value={assignment.submissionDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Details"
                name="details"
                value={assignment.details}
                onChange={handleChange}
                multiline
                rows={4}
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Button
  variant="contained"
  type="submit"
  onClick={handleSubmit}
  sx={{
    minWidth: 200,
    backgroundColor: '#063552',   // 🟠 orange
    fontWeight: 600,
    textTransform: 'uppercase',   // CAPITAL LETTERS
    '&:hover': {
      backgroundColor: '#2c6385', // darker orange on hover
    },
  }}
>
  CREATE ASSIGNMENT
</Button>

            <Button variant="contained" color="error" type="button" onClick={handleCancelModal} sx={{ minWidth: 200 }}>
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#063552'
, textAlign: 'center' }}>
            VIEW ASSIGNMENTS

          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Teacher</InputLabel>
                <Select
                  value={selectedTeacher}
                  onChange={handleTeacherChange}
                  onFocus={() => fetchTeachers()}
                  label="Teacher"
                >
                  <MenuItem value="">Select Teacher</MenuItem>
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                      {teacher.employeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedTeacher}>
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={handleClassChange}
                  onFocus={() => selectedTeacher && fetchClasses()}
                  label="Class"
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedClass}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={selectedSection}
                  onChange={handleSectionChange}
                  onFocus={() => selectedClass && fetchSections(selectedClass)}
                  label="Section"
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec.sectionId} value={sec.sectionId}>
                      {sec.sectionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" disabled={!selectedSection || isLoadingSubjects}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                  label="Subject"
                >
                  <MenuItem value="">
                    {isLoadingSubjects ? 'Loading subjects...' : 'Select Subject'}
                  </MenuItem>
                  {subjects.map((sub) => (
                    <MenuItem key={sub.subjectId} value={sub.subjectName}>
                      {sub.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: '#063552' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Select</TableCell>
                { /* <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assignment ID</TableCell> */}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assign Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subject</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submission Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Class</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>File</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getTableContent()}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ModalComponent
        open={modalOpen}
        handleClose={() => handleDeleteConfirmed(selectedAssignmentId)}
        handleCancel={handleCancelModal}
        title="Delete Confirmation"
        message="Are you sure you want to delete this assignment?"
        onConfirm={() => handleDeleteConfirmed(selectedAssignmentId)}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CreateAssignment;